import React from 'react';
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { GameInfo } from '@/pages/game';
import TeamLogo from './team-logo';
import { formatTimestampToDayMonth } from '@/utils/time';
import { Tooltip } from "@heroui/tooltip";
import { isMobile } from '@/stores/MenuStore';


interface GamesAgainstProps {
    games: GameInfo[];
    team1_id: number;
    team1_name: string;
    team2_id: number;
    team2_name: string;
}

const GamesHeadToHead: React.FC<GamesAgainstProps> = ({ games, team1_id, team1_name, team2_id, team2_name }) => {
    const { t } = useTranslation();

    const parseScore = (score: string | null): [number, number] => {
        if (!score) return [0, 0];
    
        const normalized = score.replace('x', '-');
        const parts = normalized.split('-');
    
        const home = parseInt(parts[0], 10);
        const away = parseInt(parts[1], 10);
    
        return [isNaN(home) ? 0 : home, isNaN(away) ? 0 : away];
    };
    
    const getScoreColorClass = (home: number, away: number): [string, string] => {
        if (home > away) return ['text-primary-400', 'text-red-500'];
        if (home < away) return ['text-red-500', 'text-primary-400'];
        return ['text-default-600', 'text-default-600'];
    };

    const sortedGames = [...games].sort((a, b) => b.starting_at_timestamp - a.starting_at_timestamp);

    const totalScoreTeam = (games: GameInfo[], team_id: number) => {
        let wins = 0;
        let draws = 0;
        let losses = 0;
        for (const game of games) {
            const [homeScore, awayScore] = parseScore(game.score ?? null);
            const isHome = game.home_id === team_id;
            const isAway = game.away_id === team_id;
            if (!isHome && !isAway) continue;
            if (homeScore === awayScore) {
                draws += 1;
            } else if (
                (isHome && homeScore > awayScore) ||
                (isAway && awayScore > homeScore)
            ) {
                wins += 1;
            } else {
                losses += 1;
            }
        }
        return { wins, draws, losses };
    };

    const {
        wins: t1_wins,
        draws: t1_draws,
        losses: t1_losses
    } = totalScoreTeam(sortedGames, team1_id);
    
    const {
        wins: t2_wins,
        draws: t2_draws,
        losses: t2_losses
    } = totalScoreTeam(sortedGames, team2_id);

    const teamTableLine = (title:string, team1_value: number, team2_value: number) => {
        return <div className='flex items-center w-full'>
            <span className='flex w-full items-center text-default-500 text-sm'>{title}</span>
            <span className='flex w-full items-center justify-center font-medium'>{team1_value}</span>
            <span className='flex w-full items-center justify-center font-medium'>{team2_value}</span>
        </div>
    };

    return (
        <div className={`py-4 shadow flex w-full flex-col bg-default-100 rounded-xl fadeIn self-start ${!isMobile() && 'max-w-sm min-w-xs'}`}>
            <span className='flex text-lg font-medium text-default-900 px-5'>{t("game.game_9")}</span>
            
            <div className='flex flex-col w-full items-center gap-2 py-3 px-5'>
                <div className='flex items-center w-full'>
                    <span className='flex w-full items-center justify-center'></span>
                    <Link to={`/team/${team1_id}`} className='flex w-full'>
                        <Tooltip content={team1_name}>
                            <div className='flex w-full items-center justify-center'>
                                <TeamLogo teamId={team1_id} size={25} />
                            </div>
                        </Tooltip>
                    </Link>
                    <Link to={`/team/${team2_id}`} className='flex w-full'>
                        <Tooltip content={team2_name}>
                            <div className='flex w-full items-center justify-center'>
                                <TeamLogo teamId={team2_id} size={25} />
                            </div>
                        </Tooltip>
                    </Link>
                </div>
                {teamTableLine(t("game.game_10"), t1_wins, t2_wins)}
                {teamTableLine(t("game.game_11"), t1_losses, t2_losses)}
                {teamTableLine(t("game.game_12"), t1_draws, t2_draws)}
            </div>

            {sortedGames.map((g, idx) => {
                const [homeScore, awayScore] = parseScore(g.score ?? null);
                const [homeClass, awayClass] = getScoreColorClass(homeScore, awayScore);
                return (
                    <Link key={idx+g.id} to={`/game/${g.id}`} className='flex w-full'>
                        <div className="flex w-full hover:bg-default-200 justify-between transition gap-3 items-center py-3 px-5">
                            <span className='flex py-1 w-[30%] items-center justify-center text-sm text-center text-default-900'>{formatTimestampToDayMonth(g.starting_at_timestamp, true)}</span>
                        
                            <div className='flex justify-center items-center gap-3 w-full'>
                                <TeamLogo teamId={g.home_id} size={25} />
                                <div className="flex w-[20%] min-w-[50px] items-center justify-center rounded-xl px-2 shadow-lg bg-default-200">
                                    <span className={`${homeClass} font-bold`}>{homeScore}</span>
                                    <span className="mx-1 text-default-600">x</span>
                                    <span className={`${awayClass} font-bold`}>{awayScore}</span>
                                </div>
                                <TeamLogo teamId={g.away_id} size={25} />
                            </div>
                        
                            <span className='flex w-[30%] text-xs text-default-900'>{g.league_name}</span>
                        </div>
                    </Link>
                );
            })}
        </div>
    );
};

export default GamesHeadToHead;
